import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Upload, X, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth, ROLES } from "@/contexts/AuthContext";
import { api, errMsg, auth } from "@/lib/api";

/* --------------------------------- Config -------------------------------- */
const COLUMNS = [
  { key: "Product Name",  required: true  },
  { key: "SKU",           required: false },
  { key: "Barcode",       required: false },
  { key: "Category",      required: true  },
  { key: "Sub Category",  required: false },
  { key: "Brand",         required: false },
  { key: "MRP",           required: true  },
  { key: "Selling Price", required: true  },
  { key: "Cost Price",    required: false },
  { key: "Weight",        required: false },
  { key: "Unit",          required: false },
  { key: "Stock",         required: false },
  { key: "HSN Code",      required: false },
  { key: "GST %",         required: false },
  { key: "Description",   required: false },
  { key: "Image URL 1",   required: false },
  { key: "Image URL 2",   required: false },
  { key: "Status",        required: false },
];

const REQUIRED = COLUMNS.filter((c) => c.required).map((c) => c.key);

/* --------------------------------- Helpers ------------------------------- */
const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";
const norm    = (v) => String(v || "").trim().toLowerCase();
const num     = (v) => (isEmpty(v) ? null : Number(v));

function buildTemplateSheet() {
  const headers = COLUMNS.map((c) => (c.required ? `${c.key} *` : c.key));
  const sample = [
    "Amul Milk 1L", "SKU001", "8901234567890", "Dairy", "Milk",
    "Amul", "70", "62", "55", "1", "L", "50",
    "0401", "5", "Full cream milk", "https://...", "", "active",
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, sample]);
  ws["!cols"] = headers.map(() => ({ wch: 18 }));
  return ws;
}

// CSV escaping helper for error report download
function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/* ---------------------------------- Page --------------------------------- */
export default function BulkProductUpload() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const allowed = isSuperAdmin || user?.role === ROLES.STORE_ADMIN || user?.role === ROLES.STORE_OWNER;

  const fileInputRef = useRef(null);
  const [file, setFile]       = useState(null);
  const [rows, setRows]       = useState([]); // enriched rows
  const [parsing, setParsing] = useState(false);

  // Lookup catalogs — loaded on Preview
  const [catalog, setCatalog] = useState({ cats: [], brands: [], skuMap: new Map() });

  // SUPER_ADMIN — pick target store
  const [stores, setStores]   = useState([]);
  const [storeId, setStoreId] = useState("");

  // Import state
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [result,    setResult]    = useState(null); // { imported, updated, skipped, failed:[{row, reason}] }

  // Load stores for SUPER_ADMIN
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/stores?limit=100").then((r) => {
      const items = r.data?.items || r.data || [];
      setStores(items);
      const active = auth.getStoreIdFor("SUPER_ADMIN") || items[0]?.id || "";
      setStoreId(active);
    }).catch(() => {});
  }, [isSuperAdmin]);

  // When SUPER_ADMIN switches store, persist to localStorage so ALL api calls
  // (including the interceptor auto-inject) route to the chosen tenant.
  const onStoreChange = (id) => {
    setStoreId(id);
    if (isSuperAdmin && id) auth.setStoreId(id, "SUPER_ADMIN");
  };

  /* ---------- Template Download ---------- */
  const downloadXlsx = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildTemplateSheet(), "Products");
    XLSX.writeFile(wb, "bulk_product_template.xlsx");
  };
  const downloadCsv = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, buildTemplateSheet(), "Products");
    XLSX.writeFile(wb, "bulk_product_template.csv", { bookType: "csv" });
  };

  /* ---------- File Upload & Parse ---------- */
  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "csv"].includes(ext)) {
      toast.error("Only .xlsx or .csv files are supported");
      return;
    }
    setFile(f);
    setRows([]);
    setResult(null);
  };

  // Header option: send X-Store-Id per request (SUPER_ADMIN targeting a store)
  const withStoreHdr = () => {
    // Also mirror to localStorage so the axios interceptor auto-injects the
    // same value on ANY internal call (e.g. inventory adjust after create).
    if (isSuperAdmin && storeId) auth.setStoreId(storeId, "SUPER_ADMIN");
    return isSuperAdmin && storeId ? { headers: { "X-Store-Id": storeId } } : {};
  };

  const handlePreview = async () => {
    if (!file) return toast.error("Please choose a file first");
    setParsing(true);
    setResult(null);
    try {
      // 1) Read file
      const buf = await file.arrayBuffer();
      const wb  = XLSX.read(buf, { type: "array" });
      const ws  = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });

      // 2) Load catalogs (categories, brands, existing SKUs) — from target store.
      //    Backend caps `limit` at 100, so paginate.
      const cfg = withStoreHdr();
      const fetchAll = async (path) => {
        const out = []; let page = 1;
        while (true) {
          const { data } = await api.get(`${path}${path.includes("?") ? "&" : "?"}limit=100&page=${page}`, cfg);
          const items = data?.items || [];
          out.push(...items);
          if (items.length < 100) break;
          page++;
          if (page > 100) break; // safety
        }
        return out;
      };
      const [cats, brands, prods] = await Promise.all([
        fetchAll("/categories").catch(() => []),
        fetchAll("/brands").catch(() => []),
        fetchAll("/products").catch(() => []),
      ]);
      const skuMap = new Map(); // sku(lower) -> id
      prods.forEach((p) => { if (p.sku) skuMap.set(norm(p.sku), p.id); });
      setCatalog({ cats, brands, skuMap });

      // 3) Normalize headers + basic required-field validation
      const enriched = raw.map((r, idx) => {
        const clean = {};
        for (const [k, v] of Object.entries(r)) {
          const key = k.replace(/\s*\*+\s*$/, "").trim();
          clean[key] = typeof v === "string" ? v.trim() : v;
        }
        const missing = REQUIRED.filter((k) => isEmpty(clean[k]));
        const reasons = missing.map((m) => `Missing ${m}`);

        // Category / Subcategory / Brand lookup — mark invalid if not found
        let category_id = null;
        let subcategory_id = null;
        let brand_id = null;

        if (!isEmpty(clean.Category)) {
          const cat = cats.find((c) => !c.parent_id && norm(c.name) === norm(clean.Category));
          if (!cat) reasons.push("Category not found");
          else category_id = cat.id;

          if (category_id && !isEmpty(clean["Sub Category"])) {
            const sub = cats.find((c) => c.parent_id === category_id && norm(c.name) === norm(clean["Sub Category"]));
            if (!sub) reasons.push("Subcategory not found");
            else subcategory_id = sub.id;
          }
        }
        if (!isEmpty(clean.Brand)) {
          const br = brands.find((b) => norm(b.name) === norm(clean.Brand));
          if (!br) reasons.push("Brand not found");
          else brand_id = br.id;
        }

        return {
          __row: idx + 2,
          __valid: reasons.length === 0,
          __reasons: reasons,
          __resolved: { category_id, subcategory_id, brand_id },
          ...clean,
        };
      });

      // 4) Duplicate SKU / Barcode detection within file
      const fileSku = new Map(), fileBc = new Map();
      enriched.forEach((r) => {
        if (!isEmpty(r.SKU))     fileSku.set(norm(r.SKU), (fileSku.get(norm(r.SKU)) || 0) + 1);
        if (!isEmpty(r.Barcode)) fileBc.set(norm(r.Barcode), (fileBc.get(norm(r.Barcode)) || 0) + 1);
      });
      enriched.forEach((r) => {
        if (!isEmpty(r.SKU) && fileSku.get(norm(r.SKU)) > 1) {
          r.__reasons.push("Duplicate SKU (in file)");
          r.__valid = false;
        }
        if (!isEmpty(r.Barcode) && fileBc.get(norm(r.Barcode)) > 1) {
          r.__reasons.push("Duplicate Barcode (in file)");
          r.__valid = false;
        }
        // Existing SKU flag (not an error — decided at import time by toggle)
        if (!isEmpty(r.SKU) && skuMap.has(norm(r.SKU))) {
          r.__existingId = skuMap.get(norm(r.SKU));
        }
      });

      setRows(enriched);
      toast.success(`Parsed ${enriched.length} rows`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to parse file. Please use the provided template.");
    } finally {
      setParsing(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setRows([]);
    setResult(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ---------- Import ---------- */
  const buildCreatePayload = (r) => {
    const { category_id, subcategory_id, brand_id } = r.__resolved;
    const images = [r["Image URL 1"], r["Image URL 2"]].filter((x) => !isEmpty(x));
    return {
      name: String(r["Product Name"]).trim(),
      sku: !isEmpty(r.SKU) ? String(r.SKU).trim() : null,
      barcode: !isEmpty(r.Barcode) ? String(r.Barcode).trim() : null,
      hsn_code: !isEmpty(r["HSN Code"]) ? String(r["HSN Code"]).trim() : null,
      description: !isEmpty(r.Description) ? String(r.Description).trim() : "",
      thumbnail_url: images[0] || null,
      mrp: Number(r.MRP),
      selling_price: Number(r["Selling Price"]),
      cost_price: num(r["Cost Price"]),
      gst_rate: Number(r["GST %"] || 0),
      weight: num(r.Weight),
      unit: !isEmpty(r.Unit) ? String(r.Unit).trim() : null,
      brand_id, category_id, subcategory_id,
      is_active: norm(r.Status) === "inactive" ? false : true,
    };
  };

  const buildUpdatePayload = (r) => {
    // Only these 6 fields allowed to be updated per spec
    const images = [r["Image URL 1"], r["Image URL 2"]].filter((x) => !isEmpty(x));
    const p = {};
    if (!isEmpty(r.MRP))            p.mrp = Number(r.MRP);
    if (!isEmpty(r["Selling Price"])) p.selling_price = Number(r["Selling Price"]);
    if (!isEmpty(r["Cost Price"])   ) p.cost_price = Number(r["Cost Price"]);
    if (!isEmpty(r.Description))    p.description = String(r.Description).trim();
    if (images.length > 0)          p.thumbnail_url = images[0];
    return p; // Stock handled via /inventory/adjust
  };

  const handleImport = async () => {
    if (!rows.length) return;
    const valid = rows.filter((r) => r.__valid);
    if (!valid.length) return toast.error("No valid rows to import");

    setImporting(true);
    setProgress(0);
    setResult(null);
    const cfg = withStoreHdr();

    let imported = 0, updated = 0, skipped = 0;
    const failed = [];

    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      try {
        if (r.__existingId) {
          if (!updateExisting) {
            skipped++;
          } else {
            const payload = buildUpdatePayload(r);
            if (Object.keys(payload).length > 0) {
              await api.patch(`/products/${r.__existingId}`, payload, cfg);
            }
            // Update stock via inventory adjust (only if stock provided) — set-to via delta from current isn't
            // possible without a GET; simplest safe path: skip stock on update.
            // Spec allows Stock in update fields — we ADJUST by the given quantity (treated as delta add).
            const stk = num(r.Stock);
            if (stk != null && stk > 0) {
              await api.post("/inventory/adjust", {
                product_id: r.__existingId, variant_id: null,
                delta_qty: stk, reason: "bulk_update",
              }, cfg);
            }
            updated++;
          }
        } else {
          const payload = buildCreatePayload(r);
          const { data } = await api.post("/products", payload, cfg);
          const stk = num(r.Stock);
          if (stk != null && stk > 0 && data?.id) {
            await api.post("/inventory/adjust", {
              product_id: data.id, variant_id: null,
              delta_qty: stk, reason: "bulk_import",
            }, cfg);
          }
          imported++;
        }
      } catch (e) {
        failed.push({ row: r, reason: errMsg(e, "Import failed") });
      }
      setProgress(Math.round(((i + 1) / valid.length) * 100));
    }

    setImporting(false);
    setResult({ imported, updated, skipped, failed });
    toast.success(`Import finished: ${imported} created, ${updated} updated, ${skipped} skipped, ${failed.length} failed`);
  };

  const downloadErrorReport = () => {
    if (!result?.failed?.length) return;
    const headers = ["Row #", ...COLUMNS.map((c) => c.key), "Failure Reason"];
    const lines = [headers.map(csvEscape).join(",")];
    for (const f of result.failed) {
      const row = [f.row.__row, ...COLUMNS.map((c) => f.row[c.key] ?? ""), f.reason];
      lines.push(row.map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bulk_import_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- Summary ---------- */
  const summary = useMemo(() => {
    const total = rows.length;
    const valid = rows.filter((r) => r.__valid).length;
    return {
      total,
      valid,
      invalid: total - valid,
      dupSku:     rows.filter((r) => r.__reasons.some((x) => x.includes("Duplicate SKU"))).length,
      dupBarcode: rows.filter((r) => r.__reasons.some((x) => x.includes("Duplicate Barcode"))).length,
      missingReq: rows.filter((r) => r.__reasons.some((x) => x.startsWith("Missing"))).length,
      catNotFound: rows.filter((r) => r.__reasons.some((x) => x.includes("Category not found") || x.includes("Subcategory not found"))).length,
      brandNotFound: rows.filter((r) => r.__reasons.includes("Brand not found")).length,
      willUpdate: rows.filter((r) => r.__valid && r.__existingId).length,
    };
  }, [rows]);

  /* ---------------------------------- UI --------------------------------- */
  if (!allowed) return <Navigate to="/admin/dashboard" replace />;

  const activeStoreName = stores.find((s) => s.id === storeId)?.name;

  return (
    <div className="space-y-5" data-testid="bulk-upload-page">
      <PageHeader
        title="Bulk Product Upload"
        subtitle="Download the template, upload your file, preview & import."
        testId="bulk-upload-header"
      />

      {/* Store selector — SUPER_ADMIN only */}
      {isSuperAdmin && (
        <Card>
          <CardContent className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1">
                <div className="text-sm font-semibold">Target Store</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  All products will be imported into this store.
                </div>
              </div>
              <div className="w-full md:w-72">
                <Select value={storeId} onValueChange={onStoreChange}>
                  <SelectTrigger data-testid="target-store-select">
                    <SelectValue placeholder="Select a store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {!isSuperAdmin && user?.store_id && (
        <div className="text-xs text-muted-foreground" data-testid="owner-store-hint">
          Importing into your store.
        </div>
      )}

      {/* Step 1 – Template */}
      <Card>
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
            <div className="flex-1">
              <div className="text-sm font-semibold">Step 1 — Download Template</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Fields marked with <span className="text-red-500 font-medium">*</span> are required.
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadXlsx} data-testid="download-xlsx-btn">
                <Download className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </Button>
              <Button variant="outline" onClick={downloadCsv} data-testid="download-csv-btn">
                <Download className="w-4 h-4 mr-2" /> CSV (.csv)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 2 – Upload */}
      <Card>
        <CardContent className="p-4 md:p-5 space-y-3">
          <div className="text-sm font-semibold">Step 2 — Upload File</div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={onPickFile}
              data-testid="bulk-file-input"
            />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="choose-file-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Choose File
            </Button>
            {file && (
              <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="file-name">
                <span className="truncate max-w-[240px]">{file.name}</span>
                <button onClick={handleCancel} className="text-muted-foreground hover:text-foreground" title="Remove">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <Button onClick={handlePreview} disabled={!file || parsing || importing} data-testid="preview-btn">
              {parsing ? "Parsing…" : "Preview"}
            </Button>
            <Button variant="ghost" onClick={handleCancel} disabled={(!file && rows.length === 0) || importing} data-testid="cancel-btn">
              Cancel
            </Button>

            <div className="ml-auto flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="update-existing"
                  checked={updateExisting}
                  onCheckedChange={setUpdateExisting}
                  disabled={importing}
                  data-testid="update-existing-toggle"
                />
                <Label htmlFor="update-existing" className="text-xs cursor-pointer">
                  Update Existing Products
                </Label>
              </div>
              <Button
                onClick={handleImport}
                disabled={importing || rows.length === 0 || summary.valid === 0 || (isSuperAdmin && !storeId)}
                data-testid="import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? `Importing… ${progress}%` : `Import ${summary.valid} Valid`}
              </Button>
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Supported: .xlsx, .csv — Only valid rows will be imported. Invalid rows are skipped.
            {isSuperAdmin && activeStoreName && (
              <> Importing into <span className="font-medium text-foreground">{activeStoreName}</span>.</>
            )}
          </div>

          {/* Progress */}
          {(importing || (result && progress > 0)) && (
            <div className="pt-1" data-testid="import-progress-wrap">
              <Progress value={progress} data-testid="import-progress" />
              <div className="text-[11px] text-muted-foreground mt-1">
                {importing ? `Uploading… ${progress}%` : `Done — ${progress}%`}
              </div>
            </div>
          )}

          {/* Result */}
          {result && !importing && (
            <div className="pt-2 flex flex-wrap items-center gap-2" data-testid="import-result">
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid="result-imported">
                Imported: {result.imported}
              </Badge>
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="result-updated">
                Updated: {result.updated}
              </Badge>
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="result-skipped">
                Skipped: {result.skipped}
              </Badge>
              <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid="result-failed">
                Failed: {result.failed.length}
              </Badge>
              {result.failed.length > 0 && (
                <Button size="sm" variant="outline" onClick={downloadErrorReport} data-testid="download-error-report-btn">
                  <Download className="w-4 h-4 mr-2" /> Download Error Report (CSV)
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3 – Preview */}
      {rows.length > 0 && (
        <Card>
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-sm font-semibold">Step 3 — Preview</div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" data-testid="summary-total">Total: {summary.total}</Badge>
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid="summary-valid">Valid: {summary.valid}</Badge>
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid="summary-invalid">Invalid: {summary.invalid}</Badge>
                <Badge variant="outline" data-testid="summary-dup-sku">Dup SKU: {summary.dupSku}</Badge>
                <Badge variant="outline" data-testid="summary-dup-barcode">Dup Barcode: {summary.dupBarcode}</Badge>
                <Badge variant="outline" data-testid="summary-missing-req">Missing Req: {summary.missingReq}</Badge>
                <Badge variant="outline" data-testid="summary-cat-nf">Category/Sub NF: {summary.catNotFound}</Badge>
                <Badge variant="outline" data-testid="summary-brand-nf">Brand NF: {summary.brandNotFound}</Badge>
                {summary.willUpdate > 0 && (
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="summary-will-update">
                    Will {updateExisting ? "Update" : "Skip"} (existing): {summary.willUpdate}
                  </Badge>
                )}
              </div>
            </div>

            <div className="overflow-x-auto border rounded-lg" data-testid="preview-table-wrap">
              <table className="min-w-full text-xs">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="text-left px-2 py-2 sticky left-0 bg-secondary/50">#</th>
                    <th className="text-left px-2 py-2">Status</th>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className="text-left px-2 py-2 whitespace-nowrap">
                        {c.key}{c.required && <span className="text-red-500"> *</span>}
                      </th>
                    ))}
                    <th className="text-left px-2 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={i}
                      className={r.__valid ? "bg-green-50/60" : "bg-red-50/70"}
                      data-testid={`preview-row-${i}`}
                    >
                      <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground sticky left-0 bg-inherit">
                        {r.__row}
                      </td>
                      <td className="px-2 py-1.5">
                        {r.__valid ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Valid
                            {r.__existingId && (
                              <Badge className="ml-1 h-4 text-[9px] bg-blue-100 text-blue-700">
                                {updateExisting ? "update" : "skip"}
                              </Badge>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700">
                            <AlertTriangle className="w-3.5 h-3.5" /> Invalid
                          </span>
                        )}
                      </td>
                      {COLUMNS.map((c) => (
                        <td key={c.key} className="px-2 py-1.5 whitespace-nowrap max-w-[160px] truncate" title={String(r[c.key] ?? "")}>
                          {String(r[c.key] ?? "")}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-red-700 whitespace-nowrap">
                        {r.__reasons.join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
